import { get, post } from "aws-amplify/api";

export interface SubscriptionItem {
  subscriptionId: string;
  priceId: string;
  productName: string | null;
  featureKey: string | null;
  quantity: number;
  nickname?: string | null;
  status?: string;
  cancelAtPeriodEnd?: boolean;
}

// Items indexed by productId
export type SubscriptionItems = Record<string, SubscriptionItem>;

export interface SubscriptionEntitlements {
  features: Record<string, boolean>;
  limits?: Record<string, number>;
  tokenLimit?: number; // Max tokens per billing period
  products: string[];
}

export interface SubscriptionStatus {
  subscriptionId?: string;
  userId: string;
  customerId?: string;
  status: 'active' | 'canceled' | 'past_due' | 'trialing' | 'incomplete' | 'no_subscription';
  items?: SubscriptionItems;
  entitlements?: SubscriptionEntitlements;
  cancelAtPeriodEnd?: boolean;
  currentPeriodEnd?: number;
  customerEmail?: string;
  livemode?: boolean;
  updatedAt?: string;
}

// Get user's current subscription status from DB
export const getSubscriptionStatus = async (): Promise<SubscriptionStatus> => {
  try {
    const restOperation = get({
      apiName: "stripesystemapi",
      path: "/stripe/subscription-status",
    });

    const operationResponse = await restOperation.response;
    const status = await operationResponse.body.json();
    return status as unknown as SubscriptionStatus;
  } catch (error) {
    console.error("Error fetching subscription status:", error);
    throw error;
  }
};

// ---------------------------------------------------------------------------
// Asset limits by membership tier
// ---------------------------------------------------------------------------

/** Membership tiers that gate how many assets a user may create. */
export type AssetTier = 'essential' | 'pro' | 'no_subscription';

/**
 * Max number of assets allowed per tier — single source of truth. These numbers
 * are expected to change, so edit them here and nowhere else. Groups are not capped.
 */
export const ASSET_LIMIT_BY_TIER: Record<AssetTier, number> = {
  essential: 3,
  pro: 25,
  no_subscription: 3,
};

/** Human-readable plan name per tier, for user-facing limit copy. */
export const PLAN_LABEL_BY_TIER: Record<AssetTier, string> = {
  essential: 'Essential',
  pro: 'Pro',
  no_subscription: 'current',
};

/** Stripe product featureKey → tier. */
const FEATURE_KEY_TO_TIER: Record<string, AssetTier> = {
  'analyst-pro': 'pro',
  'analyst-essential': 'essential',
};

/** Subscription statuses that entitle a user to their plan's (paid) asset limit. */
const ENTITLED_STATUSES: SubscriptionStatus['status'][] = ['active', 'trialing'];

/**
 * Resolve a user's asset tier from their subscription status. Anything without an
 * entitled subscription and a recognised featureKey falls back to "no subscription".
 */
export const getAssetTier = (status?: SubscriptionStatus | null): AssetTier => {
  if (!status || !ENTITLED_STATUSES.includes(status.status) || !status.items) return 'no_subscription';
  const tiers = Object.values(status.items)
    .map((item) => (item.featureKey ? FEATURE_KEY_TO_TIER[item.featureKey] : undefined))
    .filter((tier): tier is AssetTier => Boolean(tier));
  // Prefer the most generous tier the user holds.
  if (tiers.includes('pro')) return 'pro';
  if (tiers.includes('essential')) return 'essential';
  return 'no_subscription';
};

export interface AssetLimitCheck {
  /** Whether creating `assetsToAdd` more assets stays within the tier cap. */
  allowed: boolean;
  tier: AssetTier;
  limit: number;
  currentCount: number;
  /** Assets remaining before the cap is hit (never negative). */
  remaining: number;
}

/**
 * Check whether a user may create more assets given their subscription status and
 * current asset count. Shared by every asset-creation flow (QuickSetup and the AI
 * Analyst chat proposal) so all paths enforce one cap.
 */
export const checkAssetLimit = (
  status: SubscriptionStatus | null | undefined,
  currentCount: number,
  assetsToAdd = 1,
): AssetLimitCheck => {
  const tier = getAssetTier(status);
  const limit = ASSET_LIMIT_BY_TIER[tier];
  return {
    allowed: currentCount + assetsToAdd <= limit,
    tier,
    limit,
    currentCount,
    remaining: Math.max(0, limit - currentCount),
  };
};

// ---------------------------------------------------------------------------
// Processed model limits by membership tier
// ---------------------------------------------------------------------------

// Tier cap switched off for now (docs/followups/CREATOR_MODE.md) — set true to enforce MODEL_LIMIT_BY_TIER again.
export const PROCESSED_MODEL_TIER_CAP_ENABLED = false;

// TODO(backend): this cap is frontend-only — a direct createProcessedModelSchema GraphQL call bypasses it, so enforce it in a resolver/Lambda before relying on it for billing.
// Max ProcessedModelSchemas per tier — edit here and nowhere else. Zero means the tier can't deploy at all.
export const MODEL_LIMIT_BY_TIER: Record<AssetTier, number> = {
  essential: 3,
  pro: 10,
  no_subscription: 0,
};

export interface ProcessedModelLimitCheck {
  /** Whether creating `modelsToAdd` more models stays within the tier cap. */
  allowed: boolean;
  tier: AssetTier;
  limit: number;
  currentCount: number;
  /** Models remaining before the cap is hit (never negative). */
  remaining: number;
  /** True when the tier can't deploy any models — the UI shows "paid plan required" instead of "limit reached". */
  requiresPaidPlan: boolean;
}

/**
 * Check whether a user may deploy more processed models given their subscription status and current
 * model count. Shared by every deploy flow (Model Garden wizard and the AI chat deploy card) so all
 * paths enforce one cap. Org-owned deploys count against the creating user's cap.
 */
export const checkProcessedModelLimit = (
  status: SubscriptionStatus | null | undefined,
  currentCount: number,
  modelsToAdd = 1,
): ProcessedModelLimitCheck => {
  const tier = getAssetTier(status);
  const limit = MODEL_LIMIT_BY_TIER[tier];
  // Guard against a negative/NaN count reaching the maths from an unloaded selector.
  const safeCount = Number.isFinite(currentCount) ? Math.max(0, currentCount) : 0;
  return {
    allowed: safeCount + modelsToAdd <= limit,
    tier,
    limit,
    currentCount: safeCount,
    remaining: Math.max(0, limit - safeCount),
    requiresPaidPlan: limit === 0,
  };
};

// Duplicate subscription info
export interface DuplicateSubscription {
  productId: string;
  productName: string | null;
  existingSubscriptionId: string;
  duplicateSubscriptionId: string;
}

// Response from Stripe direct API call
export interface StripeSubscriptionResponse {
  status: string;
  customerId?: string;
  subscriptionCount?: number;
  items: SubscriptionItems;
  duplicates?: DuplicateSubscription[];
}

// Get subscriptions directly from Stripe (source of truth)
export const getSubscriptionsFromStripe = async (): Promise<StripeSubscriptionResponse> => {
  try {
    const restOperation = get({
      apiName: "stripesystemapi",
      path: "/stripe/subscriptions-from-stripe",
    });

    const operationResponse = await restOperation.response;
    const data = await operationResponse.body.json();

    return data as unknown as StripeSubscriptionResponse;
  } catch (error) {
    console.error("Error fetching subscriptions from Stripe:", error);

    throw error;
  }
};

// Create a Stripe billing portal session
export const createPortalSession = async (): Promise<{ url: string }> => {
  try {
    const restOperation = post({
      apiName: "stripesystemapi",
      path: "/stripe/create-portal-session",
      options: {
        body: {}
      }
    });

    const operationResponse = await restOperation.response;
    const session = await operationResponse.body.json();
    return session as unknown as { url: string };
  } catch (error) {
    console.error("Error creating portal session:", error);
    throw error;
  }
};

// Admin: Get Stripe subscription for any user by customerId
export const getAdminUserStripeSubscription = async (customerId: string): Promise<StripeSubscriptionResponse> => {
  try {
    const restOperation = get({
      apiName: "stripesystemapi",
      path: `/stripe/admin/user-stripe-subscription?customerId=${encodeURIComponent(customerId)}`,
    });

    const operationResponse = await restOperation.response;
    const data = await operationResponse.body.json();

    return data as unknown as StripeSubscriptionResponse;
  } catch (error) {
    console.error("Error fetching admin user subscription:", error);
    throw error;
  }
};

// For local testing
export const getSubscriptionStatusLocal = async (userId: string): Promise<SubscriptionStatus> => {
  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
  
  try {
    const response = await fetch(`${API_URL}/api/stripe/subscription-status?userId=${userId}`);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const status = await response.json();
    return status as SubscriptionStatus;
  } catch (error) {
    console.error("Error fetching subscription status locally:", error);
    throw error;
  }
};